import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

const SECTION_IDS = ["hero", "services", "partners", "process", "regions", "contact"] as const;
export const SectionQuickNav = () => {
  const { t } = useTranslation();
  const [activeId, setActiveId] = useState<string | null>(null);

  const sections = useMemo(
    () => [
      { id: "hero", label: t("nav.home") },
      { id: "services", label: t("nav.services") },
      { id: "partners", label: t("nav.partners") },
      { id: "process", label: t("nav.process") },
      { id: "regions", label: t("nav.regions") },
      { id: "contact", label: t("nav.contact") },
    ],
    [t]
  );

  useEffect(() => {
    const handleScroll = () => {
      const viewportMid = window.innerHeight / 2;
      const current = SECTION_IDS.reduce<{ id: string; distance: number } | null>((acc, id) => {
        const element = document.getElementById(id);
        if (!element) {
          return acc;
        }
        const rect = element.getBoundingClientRect();
        const distance = Math.abs(rect.top - viewportMid);
        if (!acc || distance < acc.distance) {
          return { id, distance };
        }
        return acc;
      }, null);

      setActiveId(current?.id ?? null);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavigate = (id: string) => {
    const target = document.getElementById(id);
    if (target) {
      target.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="pointer-events-none fixed right-4 top-1/2 z-40 hidden lg:block -translate-y-1/2">
      <div className="w-48 rounded-3xl border border-white/15 bg-neutral-900/60 p-3 backdrop-blur md:w-52">
        <nav className="flex flex-col gap-2 overflow-hidden">
          {sections.map((section) => {
            const isActive = activeId === section.id;
            return (
              <button
                key={section.id}
                type="button"
                onClick={() => handleNavigate(section.id)}
                className={`pointer-events-auto w-full rounded-2xl px-3 py-2 text-sm font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-turquoise/40 ${
                  isActive
                    ? "bg-brand-turquoise/25 text-brand-turquoise"
                    : "text-white/70 hover:bg-white/10 hover:text-white"
                }`}
                aria-current={isActive}
              >
                {section.label}
              </button>
            );
          })}
        </nav>
      </div>
    </div>
  );
};
